import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface CalendarProps {
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
  availableDates?: Set<string> | null;
  allDaysSelectable?: boolean;
}
export function Calendar({
  selectedDate,
  onSelectDate,
  availableDates,
  allDaysSelectable = false
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const today = new Date();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = new Date(year, month).toLocaleDateString('es-MX', {
    month: 'long',
    year: 'numeric'
  });
  const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };
  const handleNextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };
  const formatDateKey = (d: number) => {
    const m = String(month + 1).padStart(2, '0');
    const day = String(d).padStart(2, '0');
    return `${year}-${m}-${day}`;
  };
  const renderDays = () => {
    const days = [];
    // Empty cells
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" data-id="element-831" />);
    }
    // Days
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const isToday = date.toDateString() === today.toDateString();
      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isSelected = selectedDate?.toDateString() === date.toDateString();
      const dateKey = formatDateKey(d);
      const hasSlots = availableDates ? availableDates.has(dateKey) : false;
      // In admin mode (allDaysSelectable), all non-past days are clickable
      // In public mode, only available dates (or weekends as fallback) are clickable
      const isAvailable = allDaysSelectable ? true : availableDates ? availableDates.has(dateKey) : isWeekend;
      const isEnabled = !isPast && isAvailable;
      let classes = 'aspect-square flex items-center justify-center rounded-lg text-sm transition-all relative border border-transparent ';
      if (isPast || !isAvailable) {
        classes += 'text-earth/20 cursor-not-allowed';
      } else if (isSelected) {
        classes += 'bg-earth text-cream border-earth font-semibold';
      } else if (allDaysSelectable && hasSlots) {
        // Admin mode: days with configured slots get a distinct style
        classes += 'bg-sage/15 text-earth font-medium cursor-pointer hover:bg-sage/25 hover:border-sage/40';
      } else {
        classes += 'bg-sage/10 text-earth font-medium cursor-pointer hover:bg-white hover:border-mist';
      }
      if (isToday && !isPast && !isSelected && isAvailable) {
        classes += ' border-sage text-sage font-bold';
      }
      days.push(<button key={d} disabled={!isEnabled} onClick={() => onSelectDate(date)} className={classes} data-id="element-832">
          {d}
          {hasSlots && !isSelected && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-sage rounded-full" data-id="element-833" />}
          {isSelected && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-sage-light rounded-full" data-id="element-834" />}
        </button>);
    }
    return days;
  };
  return <div className="bg-sand rounded-2xl p-5 mb-6" data-id="element-835">
      <div className="flex justify-between items-center mb-5" data-id="element-836">
        <button onClick={handlePrevMonth} className="w-9 h-9 flex items-center justify-center rounded-lg border border-mist text-earth hover:bg-white transition-colors" aria-label="Mes anterior" data-id="element-837">
          <ChevronLeft size={18} data-id="element-838" />
        </button>
        <span className="font-display text-lg text-earth font-semibold" data-id="element-839">
          {capitalizedMonth}
        </span>
        <button onClick={handleNextMonth} className="w-9 h-9 flex items-center justify-center rounded-lg border border-mist text-earth hover:bg-white transition-colors" aria-label="Mes siguiente" data-id="element-840">
          <ChevronRight size={18} data-id="element-841" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-842">
        {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-xs font-semibold text-stone uppercase tracking-wider py-1" data-id="element-843">
            {day}
          </div>)}
      </div>

      <div className="grid grid-cols-7 gap-1" data-id="element-844">{renderDays()}</div>
    </div>;
}